import { ApplicationConfig, importProvidersFrom, provideExperimentalZonelessChangeDetection, APP_INITIALIZER, isDevMode } from '@angular/core';
import { provideRouter, withViewTransitions } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { provideServiceWorker } from '@angular/service-worker';
import { GamePluginRegistry, WheelPlugin, ScratchcardPlugin, GameBoardPlugin, DiceRollPlugin, PollPlugin, SportsPlugin } from '@kanektech.com/game-engine';
import { ExperienceUpdateService } from '@kanek/shared-data-access';
import { appRoutes } from './app.routes';
import { IonicStorageModule } from '@ionic/storage-angular';

/**
 * Initialize game engine plugins
 * Registers all available game type plugins with the GamePluginRegistry singleton.
 * Checks for existing plugins to prevent duplicate registration during HMR (Hot Module Replacement).
 * @param registry - GamePluginRegistry singleton instance
 * @returns Initialization function for APP_INITIALIZER
 */
function initializeGameEngine(registry: GamePluginRegistry) {
  return () => {
    // Register game plugins
    if (!registry.hasPlugin('wheel')) {
      registry.register(new WheelPlugin());
    }
    if (!registry.hasPlugin('scratchcard')) {
      registry.register(new ScratchcardPlugin());
    }
    if (!registry.hasPlugin('game-board')) {
      registry.register(new GameBoardPlugin());
    }
    if (!registry.hasPlugin('dice-roll')) {
      registry.register(new DiceRollPlugin());
    }
    if (!registry.hasPlugin('poll')) {
      registry.register(new PollPlugin());
    }
    if (!registry.hasPlugin('sports')) {
      registry.register(new SportsPlugin());
    }
    console.log('[Experience App] Game engine initialized');
  };
}

/**
 * Initialize PWA update service
 * Ensures the service is instantiated and starts monitoring for updates
 * @param updateService - ExperienceUpdateService singleton instance
 * @returns Initialization function for APP_INITIALIZER
 */
function initializePWA(updateService: ExperienceUpdateService) {
  return () => {
    console.log('[Experience App] PWA update service initialized');
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes, withViewTransitions()),
    provideAnimationsAsync(),
    {
      provide: APP_INITIALIZER,
      useFactory: initializePWA,
      deps: [ExperienceUpdateService],
      multi: true,
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideExperimentalZonelessChangeDetection(),
    provideNativeDateAdapter(),
    importProvidersFrom(IonicStorageModule.forRoot()),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeGameEngine,
      deps: [GamePluginRegistry],
      multi: true,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        floatLabel: 'never',
        subscriptSizing: 'dynamic',
      },
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 3000,
        verticalPosition: 'top'
      }
    }
  ],
};
